import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useEscapeStack } from "components/hooks/useEscapeStack";
import { AssetIcon, MODEL_COLORS, NUMBERED_ASSET_COUNT } from "./AssetIcon";

interface AppearancePickerProps {
  currentColor: string | undefined;
  onColorChange: (color: string) => void;
  isOpen: boolean;
  onClose: () => void;
  disabled?: boolean;
  currentIcon?: number;
  onIconChange?: (icon: number) => void;
  /** When set, render via portal anchored to this element (fixed position,
   *  flips above when near the viewport bottom) — escapes overflow-clipping
   *  scroll containers. Without it: inline absolute, as before. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Swatch vocabulary. Defaults to `MODEL_COLORS`, whose entries ARE color
   *  values; spaces store palette short-names, so they pass their own list
   *  plus `resolveColor` to paint it. */
  colors?: string[];
  resolveColor?: (color: string) => string;
}

/** Rough panel height used only to decide whether to flip above the anchor. */
const ESTIMATED_PICKER_HEIGHT = 280;

export const AppearancePicker: React.FC<AppearancePickerProps> = ({
  currentColor,
  onColorChange,
  isOpen,
  onClose,
  disabled,
  currentIcon,
  onIconChange,
  anchorRef,
  colors,
  resolveColor,
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  useEscapeStack(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        // Anchor clicks are the opener's business — closing here would
        // race the click handler into a close-reopen flash.
        !anchorRef?.current?.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const palette = colors ?? MODEL_COLORS;
  const effectiveColor =
    currentColor && palette.includes(currentColor) ? currentColor : undefined;

  // Spaces have no icon, only a color — offering no handler is how a caller
  // says so, rather than a flag that can disagree with the handler.
  const hasIcon = !!onIconChange;

  let positionStyle: React.CSSProperties = {
    position: "absolute",
    left: 0,
    top: "100%",
    zIndex: 50,
  };
  if (anchorRef?.current) {
    const rect = anchorRef.current.getBoundingClientRect();
    const flipUp =
      rect.bottom + ESTIMATED_PICKER_HEIGHT > window.innerHeight &&
      rect.top > ESTIMATED_PICKER_HEIGHT;
    positionStyle = {
      position: "fixed",
      left: rect.left,
      ...(flipUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      zIndex: 9999,
    };
  }

  const panel = (
    <div
      ref={pickerRef}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="sidebar-dropdown dropdown-from-top cursor-default"
      style={{
        ...positionStyle,
        width: `${4 * 22 + 3 * 4 + 2 * 10}px`,
      }}
    >
      <div className="p-2.5 space-y-2">
        {hasIcon && (
          <div>
            <span className="text-fg-muted-medium item-height block">Icon</span>
            <div className="grid grid-cols-4 gap-1">
              {Array.from({ length: NUMBERED_ASSET_COUNT }, (_, i) => i).map(
                (index) => {
                  const isActive = index === currentIcon;
                  return (
                    <button
                      type="button"
                      key={index}
                      disabled={disabled}
                      onClick={() => onIconChange?.(index)}
                      className={[
                        "flex items-center justify-center rounded-app transition p-1 border cursor-pointer",
                        isActive
                          ? ""
                          : "border-transparent hover:border-border",
                      ].join(" ")}
                      style={
                        isActive
                          ? {
                              borderColor:
                                effectiveColor || "var(--color-accent)",
                            }
                          : undefined
                      }
                      aria-label={`Select icon ${index + 1}`}
                    >
                      <AssetIcon
                        size="sm"
                        color={effectiveColor}
                        iconIndex={index}
                      />
                    </button>
                  );
                },
              )}
            </div>
          </div>
        )}

        {hasIcon && <div className="border-t border-border" />}

        <div>
          <span className="text-fg-muted-medium item-height block">Color</span>
          <div className="grid grid-cols-4 gap-1">
            {palette.map((color) => {
              const isActive = color === (effectiveColor || "");
              return (
                <button
                  type="button"
                  key={color}
                  onClick={() => onColorChange(color)}
                  disabled={disabled}
                  className="flex items-center justify-center p-0.5 cursor-pointer"
                  aria-label={`Select color ${color}`}
                >
                  <div
                    className={`w-4 h-4 rounded-sm shrink-0 border transition ${
                      isActive
                        ? "border-accent"
                        : "border-transparent hover:border-accent/40"
                    }`}
                    style={{
                      backgroundColor: resolveColor
                        ? resolveColor(color)
                        : color,
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return anchorRef ? createPortal(panel, document.body) : panel;
};
